import { API_TAGS } from '@/shared/api/api-tags';
import { baseApi } from '@/shared/api/base-api';
import { unwrapPro20Response } from '@/shared/api/pro20-transform';
import type { Pro20ApiResponse } from '@/shared/types/api-response.types';

import type { Pro20User, SignInWithUserCodeRequest } from '../types/auth.types';

export const authApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    signInWithUserCode: builder.mutation<Pro20User, SignInWithUserCodeRequest>({
      query: (body) => ({
        url: '/auth/sign-in-with-user-code',
        method: 'POST',
        body: {
          userCode: body.userCode,
          password: body.password,
        },
      }),
      transformResponse: (response: Pro20ApiResponse<Pro20User>) =>
        unwrapPro20Response(response),
    }),

    /**
     * Impersonate / open panel as a user.
     * Body: `{ payload }` — RSA-encrypted JSON `{ userCode, email }`.
     */
    signInWithUserCodeEmail: builder.mutation<Pro20User, { payload: string }>({
      query: ({ payload }) => ({
        url: '/auth/sign-in-with-user-code-email',
        method: 'POST',
        body: { payload },
      }),
      transformResponse: (response: Pro20ApiResponse<Pro20User>) =>
        unwrapPro20Response(response),
      invalidatesTags: [API_TAGS.Auth, API_TAGS.Branch, API_TAGS.Dashboard],
    }),

    /** Update the signed-in user's password via reset endpoint. */
    changePassword: builder.mutation<
      null,
      { userCode: string; password: string }
    >({
      query: ({ userCode, password }) => {
        const params = new URLSearchParams({
          userCode,
          password,
        });
        return {
          url: `/auth/reset/password?${params.toString()}`,
          method: 'PUT',
        };
      },
      transformResponse: (response: unknown) => {
        if (response == null) {
          return null;
        }
        if (typeof response === 'object' && 'code' in response) {
          unwrapPro20Response(response as Pro20ApiResponse<unknown>);
        }
        return null;
      },
    }),
  }),
});

export const {
  useSignInWithUserCodeMutation,
  useSignInWithUserCodeEmailMutation,
  useChangePasswordMutation,
} = authApi;
